import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import styles from "./coordinates-block.module.scss";
import Button from "../../components/ui-kit/button/button";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { LandPlotsCoordinatesBlock, Points } from "../../services/reducers/master-plan-modification";
import VerifiedModal from "../../components/features/master-plan-modification-portal/modals/verified-modal/verified-modal";
import Polygon from "@arcgis/core/geometry/Polygon";
import { useAppSelector } from "../../hooks/useAppSelector";
import { debounce, isEqualWith } from "lodash";
import {
  checkForErrors,
  COORDINATE_COMPLETE_PATTERN,
  ConvertPolygonsToCoordinatesTable,
  ConvertToPolygons,
  createEmptyContour,
  getSpatialByWkid,
  hasValue,
  isPolygon,
  normalizeContour,
  reorderContours,
  trimZeros,
} from "../../utils/master-plan-helper";
import { stopDrawingTools } from "../../services/reducers/map";
import {
  hasDuplicatePoints,
  hasIntersections,
  hasIntersectionsWithInitialPlot,
  hasSelfIntersections,
  hasUnclosedRings,
  isOutsideCityBoundary,
  PlotForIntersection,
  PlotForSelfAndDuplicate,
} from "../../utils/verified-krookie-helpers";
import Dropdown from "../../components/ui-kit/dropdown/dropdown";
import { produce } from "immer";
import { LandPlot } from "../../interfaces/card";

interface showButtons {
  paste?: boolean;
  copy?: boolean;
  clear?: boolean;
  addContour?: boolean;
  getGeometry?: boolean;
  delete?: boolean;
}

interface classesCoordinatesBlock {
  container?: string;
}

export interface ValidationFlags {
  selfIntersections?: boolean;
  duplicatePoints?: boolean;
  intersections?: boolean;
  initialIntersections?: boolean;
  outsideCityBoundary?: { cityId: number };
}

interface ValidationPayload {
  current: PlotForSelfAndDuplicate[];
  all?: PlotForIntersection[];
  initial?: LandPlot[];
}

interface ValidationUI {
  modalTitle: string;
  modalMessage: string;
  onResetChanges: (id: number) => void;
}

interface ValidationOptions {
  flags: ValidationFlags;
  data: ValidationPayload;
  ui: ValidationUI;
}

interface ChangeSpatialReferenceConfig {
  options: Map<number, string>;
  onChangeSR: (wkid: number, plotIdx: number) => void;
}

interface Props {
  verified: boolean;
  setVerified: (arg: boolean) => void;
  initialData: LandPlotsCoordinatesBlock;
  setVisibleCoordinates?: (visible: boolean) => void;
  showButtons?: showButtons;
  plotNumber?: string;
  classes?: classesCoordinatesBlock;
  onSetGeometries?: (payload: {
    newGeometry: Map<number, Polygon>;
    stringGeometry: ReturnType<typeof ConvertPolygonsToCoordinatesTable>;
    clearSelection?: boolean;
  }) => void;
  onSetCoordinatesData?: (value: LandPlotsCoordinatesBlock) => void;
  onGetGeometry?: (plotIdx: number, contourIdx: number) => void;
  onDeletePlot?: (id: number) => void;
  titleTable?: string;
  validationOptions?: ValidationOptions;
  disabled?: boolean;
  changeSpatialReferenceConfig?: ChangeSpatialReferenceConfig;
  disabledStopDrawingToolsAfterVerify?: boolean;
  disableResetOnDrawingTools?: boolean;
  addEmptyContourOnMount?: boolean;
  visibleDeletePlotBtn?: boolean;
}

export type CheckForErrorsFunction = (
  data: LandPlotsCoordinatesBlock,
) => LandPlotsCoordinatesBlock;

interface FocusTarget {
  key: string;
  caret: number | null;
}

const geometriesAreEqual = (
  prev: LandPlotsCoordinatesBlock,
  curr: LandPlotsCoordinatesBlock,
) => {
  return isEqualWith(prev, curr, (_: unknown, __: unknown, key) =>
    key === "id" ? true : undefined,
  );
};

const plotGeometryChanged = (
  prevPlot: LandPlotsCoordinatesBlock[number] | undefined,
  currPlot: LandPlotsCoordinatesBlock[number],
) => {
  if (!prevPlot) return false;
  return !geometriesAreEqual([prevPlot], [currPlot]);
};

const COORDINATE_INPUT_PATTERN = /^-?(?:\d+(?:\.\d*)?|\.?\d*)?$/;

const CoordinatesBlock = ({
  verified,
  setVerified,
  initialData,
  setVisibleCoordinates,
  showButtons = {
    paste: true,
    copy: true,
    clear: true,
    addContour: true,
    getGeometry: true,
    delete: true,
  },
  plotNumber,
  classes = {},
  onSetCoordinatesData,
  onGetGeometry,
  onSetGeometries,
  titleTable,
  validationOptions = {
    flags: {
      selfIntersections: false,
      duplicatePoints: false,
      intersections: false,
      initialIntersections: false,
    },
    data: {
      current: [],
    },
    ui: {
      modalMessage: "",
      modalTitle: "",
      onResetChanges: () => {},
    },
  },
  disabled = false,
  changeSpatialReferenceConfig,
  disabledStopDrawingToolsAfterVerify = true,
  disableResetOnDrawingTools = true,
  addEmptyContourOnMount = false,
  visibleDeletePlotBtn = false,
  onDeletePlot,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const drawingToolsIsActive = useAppSelector(
    (state) => state.map.drawingToolsIsActive,
  );
  const layers = useAppSelector((state) => state.map.layers);
  const [verifiedModal, setVerifiedModal] = useState<boolean | null>(null);
  const [data, setData] = useState<LandPlotsCoordinatesBlock>(
    initialData || [],
  );
  const prevDataRef = useRef<typeof data | null>(null);
  const validatedData = useMemo(() => checkForErrors(data), [data]);
  const [verifyingPlotId, setVerifyingPlotId] = useState<number | null>(null);
  const [verifiedPlots, setVerifiedPlots] = useState<Set<number>>(new Set());
  const skipNextVerificationReset = useRef(false);

  useEffect(() => {
    if (onSetCoordinatesData) {
      onSetCoordinatesData(validatedData);
    }
  }, [validatedData]);

  const resetVerification = () => {
    setVerified(false);
    setVerifiedPlots(new Set());
    setVerifyingPlotId(null);
  };

  useEffect(() => {
    resetVerification();
  }, [data.length]);

  useEffect(() => {
    if (prevDataRef.current) {
      const prevById = new Map(prevDataRef.current.map((p) => [p.PlotId, p]));
      const changedPlotIds = data
        .filter((plot) => plotGeometryChanged(prevById.get(plot.PlotId), plot))
        .map((plot) => plot.PlotId);

      if (changedPlotIds.length > 0) {
        setVerifiedPlots((prev) => {
          if (prev.size === 0) return prev;
          const next = new Set(prev);
          changedPlotIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    }

    if (skipNextVerificationReset.current) {
      skipNextVerificationReset.current = false;
    }

    prevDataRef.current = data;
  }, [data]);

  const focusedRef = useRef<FocusTarget | null>(null);

  const inputsRef = useRef<Record<string, HTMLInputElement>>({});

  const {
    flags: {
      selfIntersections,
      duplicatePoints,
      intersections,
      outsideCityBoundary,
      initialIntersections,
    } = {},
    data: { current: currentPlots, all: allPlots, initial: initialPlots } = {},
    ui: { modalMessage = "", modalTitle = "", onResetChanges = () => {} } = {},
  } = validationOptions;

  useEffect(() => {
    if (!verifyingPlotId || !currentPlots) return;

    (async () => {
      await verifiedModalEmulated(verifyingPlotId);
    })();
  }, [verifyingPlotId, currentPlots]);

  useEffect(() => {
    if (!initialData) return;
    const normalizedData = initialData.map((plot) => ({
      ...plot,
      contours: plot.contours.map((contour) => {
        const points = contour.points.map((p) => ({ ...p }));
        normalizeContour(points);
        return {
          ...contour,
          points: points,
        };
      }),
    }));
    setData(normalizedData);
  }, [initialData]);

  useEffect(() => {
    if (!addEmptyContourOnMount || data.length === 0) return;

    const needsUpdate = data.some((plot) => plot.contours.length === 0);

    if (needsUpdate) {
      const updatedData = data.map((plot) => {
        if (plot.contours.length === 0) {
          return {
            ...plot,
            contours: [createEmptyContour()],
          };
        }
        return plot;
      });

      setData(updatedData);
    }
  }, [data.length, addEmptyContourOnMount]);

  useEffect(() => {
    if (!currentPlots || currentPlots.length === 0) {
      setVerified(false);
      return;
    }

    if (verifiedPlots.size === currentPlots.length) {
      setVerified(true);
    } else {
      setVerified(false);
    }
  }, [verifiedPlots, currentPlots]);

  const handleClickVerify = (plotId: number) => {
    skipNextVerificationReset.current = true;

    const plots = data.map((plot) => {
      const closedContours = plot.contours.filter((contour) =>
        isPolygon(contour.points.map((p) => [+p.north!, +p.east!])),
      );
      return {
        ...plot,
        closedContours,
      };
    });

    const hasClosedContours = plots.some(
      (plot) => plot.closedContours.length > 0,
    );

    if (hasClosedContours) {
      const dataWithOnlyClosedContours = data.map((plot, index) => ({
        ...plot,
        contours: plots[index].closedContours,
      }));

      onSetGeometries?.({
        newGeometry: ConvertToPolygons(dataWithOnlyClosedContours),
        stringGeometry: ConvertPolygonsToCoordinatesTable(
          dataWithOnlyClosedContours,
        ),
        clearSelection: true,
      });
    }

    setVerifyingPlotId(plotId);
  };

  const verifiedModalEmulated = async (plotId: number) => {
    if (!currentPlots) return;

    let isValid = true;

    const currentPlot = currentPlots.filter((plot) => plot.objectId === plotId);
    const otherCurrentPlots: PlotForIntersection[] = currentPlots.flatMap(
      (plot) => {
        if (plot.objectId === plotId || !plot.newGeometry) return [];
        return [{ objectId: plot.objectId, geometry: plot.newGeometry }];
      },
    );

    if (isValid) {
      const unclosed = hasUnclosedRings(currentPlot);
      if (unclosed) isValid = false;
    }

    if (selfIntersections) {
      const self = await hasSelfIntersections(currentPlot);
      if (self) isValid = false;
    }
    if (isValid && duplicatePoints) {
      const dup = await hasDuplicatePoints(currentPlot);
      if (dup) isValid = false;
    }
    if (isValid && intersections && otherCurrentPlots.length > 0) {
      const interWithCurrent = await hasIntersections(
        currentPlot,
        otherCurrentPlots,
      );
      if (interWithCurrent) isValid = false;
    }
    if (isValid && intersections && allPlots) {
      const inter = await hasIntersections(currentPlot, allPlots, initialPlots);
      if (inter) isValid = false;
    }

    if (isValid && typeof outsideCityBoundary?.cityId === "number") {
      const interEstateEn = await isOutsideCityBoundary(
        currentPlot,
        layers,
        outsideCityBoundary.cityId,
      );
      if (interEstateEn) isValid = false;
    }

    if (isValid && initialIntersections && initialPlots) {
      const intersectsWithInitial = await hasIntersectionsWithInitialPlot(
        currentPlot,
        initialPlots,
      );
      if (intersectsWithInitial) isValid = false;
    }

    if (isValid) {
      if (drawingToolsIsActive && disabledStopDrawingToolsAfterVerify) {
        dispatch(stopDrawingTools());
      }
      const newVerifiedPlots = new Set(verifiedPlots);

      newVerifiedPlots.add(plotId);
      setVerifiedPlots(newVerifiedPlots);
    } else {
      const newVerifiedPlots = new Set(verifiedPlots);

      newVerifiedPlots.delete(plotId);
      setVerifiedPlots(newVerifiedPlots);
    }

    setVerifiedModal(true);
  };

  const handleDebouncedChange = (value: LandPlotsCoordinatesBlock) => {
    onSetGeometries &&
      onSetGeometries({
        newGeometry: ConvertToPolygons(value),
        stringGeometry: ConvertPolygonsToCoordinatesTable(value),
      });
  };

  const debouncedDispatch = useMemo(
    () => debounce(handleDebouncedChange, 1000),
    [dispatch],
  );
  useEffect(() => {
    return () => {
      debouncedDispatch.cancel();
    };
  }, [debouncedDispatch]);

  const onAddContour = (plotIdx: number) => {
    resetVerification();
    if (!data) return;
    const copyOfData = structuredClone(data);
    const plot = copyOfData[plotIdx];
    plot.contours = [...plot.contours, createEmptyContour()];
    setData(copyOfData);
  };
  const clear = (plotIdx: number) => {
    resetVerification();
    if (!data) return;
    const copyOfData = structuredClone(data);
    const plot = copyOfData[plotIdx];
    plot.contours = [];
    setData(copyOfData);
    if (onSetGeometries) {
      const emptyStringGeometry = new Map<
        number,
        { rings: string[][][]; wkid: number }
      >();
      if (plot.wkid) {
        emptyStringGeometry.set(plot.PlotId, { rings: [], wkid: plot.wkid });
      }
      onSetGeometries({
        newGeometry: new Map<number, Polygon>(),
        stringGeometry: emptyStringGeometry,
      });
    }
  };

  const onChangeRow = (
    plotIdx: number,
    contourIdx: number,
    rowIndex: number,
    field: "north" | "east",
    newValue: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    resetVerification();
    if (!data) return;

    const caretPos = event.target.selectionStart ?? null;
    const focusKey = `${plotIdx}-${contourIdx}-${rowIndex}-${field}`;
    focusedRef.current = { key: focusKey, caret: caretPos };

    if (!COORDINATE_INPUT_PATTERN.test(newValue)) return;

    const updated = produce(data, (draft) => {
      const plot = draft[plotIdx];
      const contour = plot.contours[contourIdx];
      const pts = contour.points;

      const target = pts.find((pt) => pt.index === rowIndex);

      if (target) target[field] = newValue;

      normalizeContour(pts);

      const ring = pts
        .filter(
          (p) =>
            hasValue(p.north) &&
            hasValue(p.east) &&
            !isNaN(+p.north) &&
            !isNaN(+p.east),
        )
        .map((p) => [+p.north!, +p.east!] as number[]);

      if (isPolygon(ring)) {
        plot.contours = reorderContours(plot.contours, plot.wkid);
      }
    });

    setData(updated);
    debouncedDispatch(updated);
  };

  const buildStringGeometryWithWkid = (
    plotData: LandPlotsCoordinatesBlock,
  ) => {
    const stringGeometry = ConvertPolygonsToCoordinatesTable(plotData);
    plotData.forEach((p) => {
      if (!stringGeometry.has(p.PlotId) && p.wkid) {
        stringGeometry.set(p.PlotId, { rings: [], wkid: p.wkid });
      }
    });
    return stringGeometry;
  };

  const onDeleteContour = (plotIdx: number, idx: number) => {
    resetVerification();
    if (!data) return;
    const copyOfData = structuredClone(data);
    copyOfData[plotIdx].contours.splice(idx, 1);
    const plot = copyOfData[plotIdx];
    plot.contours = reorderContours(plot.contours, plot.wkid);
    setData(copyOfData);
    if (onSetGeometries) {
      onSetGeometries({
        newGeometry: ConvertToPolygons(copyOfData),
        stringGeometry: buildStringGeometryWithWkid(copyOfData),
      });
    }
  };

  const fallbackCopyToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      console.log("Fallback: Text copied successfully");
    } catch (err) {
      console.error("Fallback: Failed to copy text", err);
    }

    document.body.removeChild(textArea);
  };

  const onCopyCoordinates = (plotIdx: number) => {
    if (!data || !data.length) return;

    let counter = 0;
    let firstOnContour: number;

    const coordinatesText = data[plotIdx].contours
      .map((contour) => {
        firstOnContour = counter + 1;

        return contour.points
          .map((point, idx) => {
            if (
              point.north &&
              point.east &&
              (point.east != contour.points[0].east ||
                point.north != contour.points[0].north ||
                idx == 0)
            ) {
              counter++;
            }

            let num =
              point.north && point.east
                ? point.east == contour.points[0].east &&
                  point.north == contour.points[0].north
                  ? firstOnContour
                  : counter
                : "";

            return `${num}\t${point.north ?? ""}\t${point.east ?? ""}`;
          })
          .join("\n");
      })
      .join("\n");

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(coordinatesText)
        .then(() => console.log("Coordinates copied successfully"))
        .catch((err) => console.error("Failed to copy text:", err));
    } else {
      fallbackCopyToClipboard(coordinatesText);
    }
  };

  const onPasteCoordinates = async (plotIdx: number) => {
    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch (err) {
      console.error("Failed to read clipboard:", err);
      return;
    }

    const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return;

    const contours: { id: string; area: string; points: Points }[] = [];
    let currentPoints: Points = [];
    let firstNum: string | null = null;
    let pointIndex = 1;

    for (const line of lines) {
      const parts = line.split("\t");
      if (parts.length < 3) continue;
      const [num, north, east] = parts;

      if (firstNum === null) {
        firstNum = num.trim();
        pointIndex = 1;
      }

      const northVal = north.trim();
      const eastVal = east.trim();
      currentPoints.push({
        id: uuidv4(),
        index: pointIndex++,
        north: northVal,
        east: eastVal,
        err: !COORDINATE_COMPLETE_PATTERN.test(northVal) || !COORDINATE_COMPLETE_PATTERN.test(eastVal),
      });

      if (num.trim() === firstNum && currentPoints.length > 1) {
        normalizeContour(currentPoints);
        contours.push({ id: uuidv4(), area: "0", points: currentPoints });
        currentPoints = [];
        firstNum = null;
      }
    }

    if (currentPoints.length > 0) {
      const first = currentPoints[0];
      const last = currentPoints[currentPoints.length - 1];
      const alreadyClosed =
        last.north === first.north && last.east === first.east;
      if (!alreadyClosed && currentPoints.length >= 2) {
        currentPoints.push({
          id: uuidv4(),
          index: pointIndex++,
          north: first.north,
          east: first.east,
          err: !COORDINATE_COMPLETE_PATTERN.test(first.north ?? "") || !COORDINATE_COMPLETE_PATTERN.test(first.east ?? ""),
        });
      }
      normalizeContour(currentPoints);
      contours.push({ id: uuidv4(), area: "0", points: currentPoints });
    }

    if (!contours.length) return;

    const copyOfData = structuredClone(data);
    copyOfData[plotIdx].contours = contours;
    setData(copyOfData);
    resetVerification();
    onSetGeometries?.({
      newGeometry: ConvertToPolygons(copyOfData),
      stringGeometry: ConvertPolygonsToCoordinatesTable(copyOfData),
    });
  };

  const handleGetGeometry = (plotIdx: number, contourIdx: number) => {
    resetVerification();
    onGetGeometry && onGetGeometry(plotIdx, contourIdx);
  };

  useEffect(() => {
    if (drawingToolsIsActive) {
      if (disableResetOnDrawingTools) {
        resetVerification();
      }
    }
  }, [drawingToolsIsActive]);

  useLayoutEffect(() => {
    const f = focusedRef.current;
    if (!f) return;

    const el = inputsRef.current[f.key];
    if (!el) return;

    el.focus({ preventScroll: true });
    if (f.caret != null) {
      el.setSelectionRange(f.caret, f.caret);
    }
  }, [data]);

  const disabledDeletePlot = !(data.length > 2);

  const handleDeletePlot = (id: number) => {
    if (!onSetGeometries || !onDeletePlot) return;
    const updatedData = data.filter((plot) => plot.PlotId !== id);
    setData(updatedData);

    onSetGeometries({
      newGeometry: ConvertToPolygons(updatedData),
      stringGeometry: buildStringGeometryWithWkid(updatedData),
    });
    onDeletePlot(id);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.plotsContainer}>
        {validatedData.map((landPlot, plotIdx) => {
          let globalPointIndex = 1;

          const isVerifyDisabled =
            (validatedData.length === 1 &&
              validatedData[0].contours.length === 1 &&
              validatedData[0].contours[0].points.every(
                (p) => !p.north || !p.east,
              )) ||
            verifiedPlots.has(landPlot.PlotId);

          return (
            <div
              className={`${styles.coordinatesBlock} ${classes.container || ""} ${disabled ? styles.disabled : ""}`}
              key={landPlot.PlotId}
            >
              <div className={styles.header}>
                <div className={styles.title}>
                  {titleTable
                    ? titleTable
                    : t("coordinatesBlock.plotCoordinates", {
                        number: plotNumber ?? plotIdx + 1,
                      })}{" "}
                  {landPlot.contours?.some(
                    (contour) => contour.points.length > 0,
                  ) && (
                    <>
                      {changeSpatialReferenceConfig ? (
                        <Dropdown
                          onChange={(selected) => {
                            if (typeof selected === "number") {
                              changeSpatialReferenceConfig.onChangeSR(
                                selected,
                                plotIdx,
                              );
                            }
                          }}
                          value={landPlot.wkid}
                          options={changeSpatialReferenceConfig.options}
                          classes={{
                            dropdown: styles.dropdown,
                          }}
                        />
                      ) : (
                        landPlot.wkid && (
                          <span>({getSpatialByWkid(landPlot.wkid)})</span>
                        )
                      )}
                    </>
                  )}
                </div>
                {visibleDeletePlotBtn && (
                  <button
                    disabled={!onDeletePlot || disabledDeletePlot}
                    className={`${styles.btn} ${styles.delete}`}
                    onClick={() => handleDeletePlot(landPlot.PlotId)}
                  >
                    Delete Plot
                  </button>
                )}
              </div>
              <div
                className={styles.buttons}
                style={{ marginBottom: data.length == 0 ? "0px" : "" }}
              >
                {showButtons?.paste && (
                  <button
                    className={`${styles.btn} ${styles.paste}`}
                    onClick={() => onPasteCoordinates(plotIdx)}
                  >
                    {t("coordinatesBlock.paste")}
                  </button>
                )}
                {showButtons?.copy && (
                  <button
                    className={`${styles.btn} ${styles.copy}`}
                    onClick={() => onCopyCoordinates(plotIdx)}
                    disabled={landPlot.contours.length == 0}
                  >
                    {t("coordinatesBlock.copy")}
                  </button>
                )}
                {showButtons?.clear && (
                  <button
                    className={`${styles.btn} ${styles.clear}`}
                    onClick={() => clear(plotIdx)}
                    disabled={landPlot.contours.length == 0}
                  >
                    {t("coordinatesBlock.clear")}
                  </button>
                )}
                {showButtons?.addContour && (
                  <button
                    className={`${styles.btn} ${styles.addContour}`}
                    onClick={() => onAddContour(plotIdx)}
                  >
                    {t("coordinatesBlock.addContour")}
                  </button>
                )}
              </div>
              <div className={styles.body}>
                {landPlot.contours!.map((contour, contourIdx) => {
                  const ring = contour.points.map((p) => [+p.north!, +p.east!]);
                  const isClosed = isPolygon(ring);
                  const totalPoints = contour.points.length;
                  const incrementCount = isClosed
                    ? totalPoints - 1
                    : totalPoints;

                  return (
                    <div className={styles.contour} key={contour.id}>
                      <div className={styles.contourHeader}>
                        <div className={styles.contourHeaderInfo}>
                          <span className={styles.name}>
                            {t("coordinatesBlock.contour", {
                              number: contourIdx + 1,
                            })}
                          </span>
                          <span className={styles.area}>
                            {t("coordinatesBlock.areaWithUnit", {
                              area: contour.area,
                            })}
                          </span>
                        </div>
                        {showButtons?.getGeometry && showButtons.delete && (
                          <div className={styles.contourHeaderButtons}>
                            {showButtons?.getGeometry && (
                              <button
                                onClick={() =>
                                  handleGetGeometry(plotIdx, contourIdx)
                                }
                                className={`${styles.btn} ${styles.getGeometry}`}
                              >
                                {t("coordinatesBlock.getGeometry")}
                              </button>
                            )}
                            {showButtons?.delete && (
                              <button
                                className={`${styles.btn} ${styles.delete}`}
                                onClick={() =>
                                  onDeleteContour(plotIdx, contourIdx)
                                }
                              >
                                {t("coordinatesBlock.delete")}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className={styles.columnHeaders}>
                        <span>{t("coordinatesBlock.tableHeaders.number")}</span>
                        <span>
                          {t("coordinatesBlock.tableHeaders.northing")}
                        </span>
                        <span>
                          {t("coordinatesBlock.tableHeaders.easting")}
                        </span>
                      </div>
                      <div className={styles.contourRows}>
                        {contour.points.map((point, idx) => {
                          const keyNorth = `${plotIdx}-${contourIdx}-${point.index}-north`;
                          const keyEast = `${plotIdx}-${contourIdx}-${point.index}-east`;

                          const isLastPoint = idx === contour.points.length - 1;

                          let displayIndex: number | string = "";

                          if (!isClosed || !isLastPoint) {
                            displayIndex = globalPointIndex++;
                          } else {
                            displayIndex = globalPointIndex - incrementCount;
                          }

                          return (
                            <div key={point.id} className={styles.contourRow}>
                              <span>{displayIndex}</span>
                              <input
                                ref={(el) => {
                                  if (el) {
                                    inputsRef.current[keyNorth] = el;
                                  } else {
                                    delete inputsRef.current[keyNorth];
                                  }
                                }}
                                value={point.north ?? ""}
                                onChange={(event) => {
                                  onChangeRow(
                                    plotIdx,
                                    contourIdx,
                                    point.index,
                                    "north",
                                    event.currentTarget.value,
                                    event,
                                  );
                                }}
                                onBlur={(event) => {
                                  const formatted = trimZeros(
                                    event.target.value,
                                  );
                                  if (formatted !== event.target.value) {
                                    onChangeRow(
                                      plotIdx,
                                      contourIdx,
                                      point.index,
                                      "north",
                                      formatted,
                                      event,
                                    );
                                  }
                                  focusedRef.current = null;
                                }}
                                onFocus={(e) => {
                                  focusedRef.current = {
                                    key: keyNorth,
                                    caret: e.target.selectionStart,
                                  };
                                }}
                                onSelect={(e) => {
                                  if (
                                    focusedRef.current &&
                                    focusedRef.current.key === keyNorth
                                  ) {
                                    focusedRef.current.caret =
                                      e.currentTarget.selectionStart;
                                  }
                                }}
                                className={point.err ? styles.error : ""}
                              />
                              <input
                                ref={(el) => {
                                  if (el) {
                                    inputsRef.current[keyEast] = el;
                                  } else {
                                    delete inputsRef.current[keyEast];
                                  }
                                }}
                                value={point.east ?? ""}
                                onChange={(event) => {
                                  onChangeRow(
                                    plotIdx,
                                    contourIdx,
                                    point.index,
                                    "east",
                                    event.currentTarget.value,
                                    event,
                                  );
                                }}
                                onBlur={(event) => {
                                  const formatted = trimZeros(
                                    event.target.value,
                                  );
                                  if (formatted !== event.target.value) {
                                    onChangeRow(
                                      plotIdx,
                                      contourIdx,
                                      point.index,
                                      "east",
                                      formatted,
                                      event,
                                    );
                                  }
                                  focusedRef.current = null;
                                }}
                                onFocus={(e) => {
                                  focusedRef.current = {
                                    key: keyEast,
                                    caret: e.target.selectionStart,
                                  };
                                }}
                                onSelect={(e) => {
                                  if (
                                    focusedRef.current &&
                                    focusedRef.current.key === keyEast
                                  ) {
                                    focusedRef.current.caret =
                                      e.currentTarget.selectionStart;
                                  }
                                }}
                                className={point.err ? styles.error : ""}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {landPlot.contours.length > 0 && (
                <div className={styles.footer}>
                  <span className={styles.totalArea}>
                    <span>{t("coordinatesBlock.totalArea")}: </span>
                    <span>
                      {t("coordinatesBlock.areaWithUnit", {
                        area: landPlot.area,
                      })}
                    </span>
                  </span>
                  {setVisibleCoordinates ? (
                    <Button
                      type="withIcon"
                      className={styles.closeBtn}
                      onClick={() => setVisibleCoordinates(false)}
                    >
                      {t("coordinatesBlock.close")}
                    </Button>
                  ) : (
                    <Button
                      type="withIcon"
                      className={styles.verifyButton}
                      onClick={() => handleClickVerify(landPlot.PlotId)}
                      disabled={isVerifyDisabled}
                    >
                      {t("coordinatesBlock.verifyCoordinates")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {verifiedModal !== null && verifyingPlotId && (
          <VerifiedModal
            verified={verifiedPlots.has(verifyingPlotId)}
            setVerifiedModal={(value) => {
              setVerifiedModal(value);
              if (value === null) {
                setVerifyingPlotId(null);
              }
            }}
            modalMessage={modalMessage}
            modalTitle={modalTitle}
            onResetChanges={() => onResetChanges(verifyingPlotId)}
          />
        )}
      </div>
    </div>
  );
};

export default CoordinatesBlock;
